import React, { ReactNode } from 'react'
import { Sparkles, User, LogIn, History, MessageCircle, Eye, Gift, Ticket, Zap, Search } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import OpenAIStatus from '../OpenAIStatus'
import FootIcon from '../icons/FootIcon'

interface MobileLayoutProps {
  children: ReactNode
  onShowAuthModal: () => void
  onShowProfile: () => void
  onShowCreate: () => void
  onShowHistory: () => void
  onShowSavedFeet: () => void
  onShowDiscover: () => void
  onShowGift?: () => void
  onShowRedemption?: () => void
  onShowGlobalSearch?: () => void
  activeTab?: 'create' | 'history' | 'savedFeet' | 'discover' | null
  setAuthMode: (mode: 'signin' | 'signup') => void
  authMode?: 'signin' | 'signup'
  showFilters?: boolean
  onToggleFilters?: () => void
  prompt?: string
  onPromptChange?: (value: string) => void
  onGenerate?: () => void
  isGenerating?: boolean
  filterSection?: ReactNode
  discoverSection?: ReactNode
  canGenerate?: boolean
  remainingGenerations?: number
  uploadedImages?: File[]
  onImagesChange?: (images: File[]) => void
}

const MobileLayout: React.FC<MobileLayoutProps> = ({
  children,
  onShowAuthModal,
  onShowProfile,
  onShowCreate,
  onShowHistory,
  onShowSavedFeet,
  onShowDiscover,
  onShowGift,
  onShowRedemption,
  onShowGlobalSearch,
  activeTab = 'create',
  setAuthMode
}) => {
  const { user, profile, loading } = useAuth()

  const getTabButtonClass = (tabName: string) => {
    const isActive = activeTab === tabName
    return `flex flex-col items-center p-2 rounded-lg transition-colors ${
      isActive 
        ? 'bg-primary-50 border border-primary-200' 
        : 'hover:bg-gray-100'
    }`
  }

  const getTabIconClass = (tabName: string) => {
    const isActive = activeTab === tabName
    return `w-4 h-4 ${isActive ? 'text-primary-600' : 'text-gray-600'}`
  }

  const getTabTextClass = (tabName: string) => {
    const isActive = activeTab === tabName
    return `text-xs mt-1 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-600'}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
      {/* Mobile Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                <FootIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Toefetti</h1>
              </div>
            </div>

            {/* Search & User */}
            <div className="flex items-center space-x-3">
              {/* Global Search Button */}
              {onShowGlobalSearch && (
                <button
                  onClick={onShowGlobalSearch}
                  className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors border border-blue-200"
                  title="Search users, tags, and images"
                >
                  <Search className="w-4 h-4" />
                </button>
              )}
              
              {loading ? (
                <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
              ) : user ? (
                <button
                  onClick={onShowProfile}
                  className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center"
                >
                  <User className="w-4 h-4 text-white" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    setAuthMode('signin')
                    onShowAuthModal()
                  }}
                  className="text-xs bg-primary-500 hover:bg-primary-600 text-white px-3 py-1.5 rounded-full transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>

          {/* User Info Bar (when logged in) */}
          {user && (
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="text-sm text-gray-600">
                  {profile?.display_name || user.email?.split('@')[0]}
                </div>
                {profile?.tier === 'premium' && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">Pro</span>
                )}
              </div>
              
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <span>{profile?.generation_count || 0} images</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* OpenAI Status (Mobile) */}
      <div className="px-4 pt-3">
        <OpenAIStatus />
      </div>

      {/* Main Content */}
      <div className="px-4 py-3">
        {children}
      </div>

      {/* Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-gray-200 z-40">
        <div className="grid grid-cols-5 gap-1 p-2">
          {user ? (
            <>
              <button
                onClick={onShowCreate}
                className={getTabButtonClass('create')}
              >
                <Zap className={getTabIconClass('create')} />
                <span className={getTabTextClass('create')}>Create</span>
              </button>

              <button
                onClick={onShowHistory}
                className={getTabButtonClass('history')}
              >
                <History className={getTabIconClass('history')} />
                <span className={getTabTextClass('history')}>History</span>
              </button>

              <button
                onClick={onShowSavedFeet}
                className={getTabButtonClass('savedFeet')}
              >
                <MessageCircle className={getTabIconClass('savedFeet')} />
                <span className={getTabTextClass('savedFeet')}>Saved</span>
              </button>

              <button
                onClick={onShowDiscover}
                className={getTabButtonClass('discover')}
              >
                <Eye className={getTabIconClass('discover')} />
                <span className={getTabTextClass('discover')}>Discover</span>
              </button>

              <button
                onClick={onShowProfile}
                className="flex flex-col items-center p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <User className="w-4 h-4 text-gray-600" />
                <span className="text-xs text-gray-600 mt-1">Profile</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setAuthMode('signin')
                  onShowAuthModal()
                }}
                className="flex flex-col items-center p-3 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogIn className="w-5 h-5 text-primary-600" />
                <span className="text-xs text-primary-600 mt-1">Sign In</span>
              </button>

              <button
                onClick={() => {
                  setAuthMode('signup')
                  onShowAuthModal()
                }}
                className="flex flex-col items-center p-3 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <User className="w-5 h-5 text-primary-600" />
                <span className="text-xs text-primary-600 mt-1">Sign Up</span>
              </button>

              <button
                onClick={onShowDiscover}
                className={getTabButtonClass('discover')}
              >
                <Eye className={getTabIconClass('discover')} />
                <span className={getTabTextClass('discover')}>Discover</span>
              </button>

              {/* Global Search for unauthenticated users */}
              {onShowGlobalSearch && (
                <button
                  onClick={onShowGlobalSearch}
                  className="flex flex-col items-center p-3 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Search className="w-5 h-5 text-blue-600" />
                  <span className="text-xs text-blue-600 mt-1">Search</span>
                </button>
              )}

              {/* Placeholder for layout consistency */}
              {!onShowGlobalSearch && (
                <div className="flex flex-col items-center p-3">
                  <div className="w-5 h-5"></div>
                  <span className="text-xs text-transparent mt-1">.</span>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Gift and Redeem buttons for authenticated users */}
        {user && (onShowGift || onShowRedemption) && (
          <div className="border-t border-gray-200 p-2">
            <div className="flex space-x-2">
              {onShowGift && (
                <button
                  onClick={onShowGift}
                  className="flex-1 flex items-center justify-center space-x-2 p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  <Gift className="w-4 h-4" />
                  <span className="text-sm font-medium">Gift</span>
                </button>
              )}

              {onShowRedemption && (
                <button
                  onClick={onShowRedemption}
                  className="flex-1 flex items-center justify-center space-x-2 p-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  <Ticket className="w-4 h-4" />
                  <span className="text-sm font-medium">Redeem</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom padding to account for fixed nav */}
      <div className="h-20"></div>
    </div>
  )
}

export default MobileLayout 